import {
    getAppDsmStructure,
    getFeatureFlags,
    isFeatureFlagEnabled,
} from './../api';
import { initUserFlow } from './user-flow';
import { initAwsRum } from './rum';

/**
 * Adds logic to handle the the 'app-change' lifecycle event from single-spa.
 */
const _handleSingleSpaAppChange = () => {
    window.addEventListener('single-spa:app-change', (evt) => {
        console.log('app-change');

        if (
            evt.detail.appsByNewStatus.MOUNTED.includes('inspections')
            || evt.detail.appsByNewStatus.MOUNTED.includes('@weever/flexDSM')
            || evt.detail.appsByNewStatus.MOUNTED.includes('@weever/siteManager')
            || evt.detail.appsByNewStatus.MOUNTED.includes('@weever/opsDashboard')
            || evt.detail.appsByNewStatus.MOUNTED.includes('@weever/formFolders')
        ) {
            const removeLoadingScreen = () => {
                const loadingScreen = document.getElementById('loading-screen');
                loadingScreen?.remove();
                console.log('loading screen remove');
            };

            // so for now I'm using timeout to ensure this is shown long enough
            setTimeout(removeLoadingScreen, 1500);
        }

        // need to remove these containers when they are not mounted
        if (!evt.detail.appsByNewStatus.MOUNTED.includes('@weever/flexDSM')) {
            const canopyContainer = document.getElementById(
                'single-spa-application:@weever/flexDSM'
            );
            canopyContainer?.remove();
        }

        // need to remove these containers when they are not mounted
        if (!evt.detail.appsByNewStatus.MOUNTED.includes('@weever/siteManager')) {
            const canopyContainer = document.getElementById(
                'single-spa-application:@weever/siteManager'
            );
            canopyContainer?.remove();
        }

        // need to remove these containers when they are not mounted
        if (!evt.detail.appsByNewStatus.MOUNTED.includes('@weever/formFolders')) {
            const canopyContainer = document.getElementById(
                'single-spa-application:@weever/formFolders'
            );
            canopyContainer?.remove();
        }

        // need to remove these containers when they are not mounted
        if (!evt.detail.appsByNewStatus.MOUNTED.includes('@weever/opsDashboard')) {
            const canopyContainer = document.getElementById(
                'single-spa-application:@weever/opsDashboard'
            );
            canopyContainer?.remove();
        }

        if (!evt.detail.appsByNewStatus.MOUNTED.includes('@weever/wxPortal')) {
            const portalContainer = document.getElementById(
                'single-spa-application:@weever/wxPortal'
            );
            portalContainer?.remove();
        }
    });
};

/**
 * Adds logic to handle the the 'before-app-change' lifecycle event from single-spa.
 */
const _handleSingleSpaBeforeAppChange = (appSlug) => {
    window.addEventListener('single-spa:before-app-change', (evt) => {
        // create a transition between apps
        const bodyEl = document.querySelector('body');
        bodyEl.dispatchEvent(window.$wxAppTransitionLoadEvent);

        // app will not even mount if the feature flag is not enabled
        // so we can't check status, have to check destination URL
        if (new URL(evt.detail.newUrl).pathname.includes('/databases')) {
            getFeatureFlags(appSlug).then((featureFlags) => {
                const isFlexDSMEnabled =
                    featureFlags.includes('flexDSM') ||
                    featureFlags.includes('flexDSMLimited');

                if (!isFlexDSMEnabled) {
                    window.location.href = '/';
                }
            });
        }

        // app will not even mount if the feature flag is not enabled
        // so we can't check status, have to check destination URL
        if (new URL(evt.detail.newUrl).pathname.includes('/dashboard')) {
            isFeatureFlagEnabled('opsDashboard', appSlug).then((isEnabled) => {
                if (!isEnabled) {
                    window.location.href = '/';
                }
            });
        }

        if (!evt.detail.appsByNewStatus.MOUNTED.includes('@weever/wxPortal')) {
            // we did some custom styles upon load of wxPortal, must remove them
            const angularjsContainer = document.getElementById(
                'single-spa-application:@weever/wxPortal'
            );
            angularjsContainer?.setAttribute('style', '');

            // we added CSS via JS by creating a link element, need to remove it
            const angularjsCss = document.getElementById('wxportal-css-link');
            angularjsCss?.remove();
        }
    });
};

/**
 * Sets the current app slug as a data attribute on the DOM so that we can
 * use it to help apply custom styles for select apps.
 * @param {string} appSlug The app slug.
 * @returns void.
 */
const _addAppSlugToDom = (appSlug) =>
    document.body.setAttribute('data-app-slug', appSlug);

const _initializeGlobalEvents = () => {
    window.$wx = window.$wx || {};
    window.$wx.mfeLogoutEvent = new Event('mfe-logout');
    window.$wx.mfeLogoutCompleteEvent = new Event('mfe-logout-complete');
    window.$wx.mfeLoginCompleteEvent = new Event('mfe-login-complete');
    window.$wx.mfeProfilePhotoSetEvent = new Event('mfe-profile-photo-set');
    window.$wx.mfeNotAuthenticatedEvent = new Event('mfe-not-authenticated');
    window.$wx.mfeLanguageChangedEvent = new Event('mfe-language-changed');
};

const _initializeGlobalVars = async (appSlug) => {
    window.$wx = window.$wx || {};

    const appDsmStructure = await getAppDsmStructure(appSlug);
    window.$wx.mfeDsmStructure = appDsmStructure;

    // prevent overwriting of DSM structure
    // we may want to change how thie works later
    // because if someone changes it in-app, it won't be reflected here
    Object.freeze(window.$wx.mfeDsmStructure);
};

/**
 * Loads any async middleware required for our implementation of single-spa.
 * @param {string} appSlug The app slug.
 */
export const loadAsync = async (appSlug) => {
    return await _initializeGlobalVars(appSlug);
};

/**
 * Loads any middleware required for our implementation of single-spa.
 * @param {string} appSlug The app slug.
 */
export const load = (appSlug) => {
    void initUserFlow(appSlug);
    void initAwsRum(appSlug);
    _initializeGlobalEvents();
    _handleSingleSpaAppChange();
    _handleSingleSpaBeforeAppChange(appSlug);
    _addAppSlugToDom(appSlug);
};
